import {
  istGast,
  kannMitarbeiten,
  PERSON_ROLE_LABELS,
  PERSON_ROLES,
  SOURCE_LABELS,
  type PersonRole,
} from '@netcon-work/shared'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { benutzerQuery, gastVorschlaegeQuery, sitzungQuery, type Benutzer } from '../lib/queries'
import { IntranetZuordnung } from './IntranetZuordnung'
import { PersonenZusammenfuehren } from './PersonenZusammenfuehren'
import { trpc } from '../lib/trpc'
import { Button, Card, CardPad } from './ui/primitives'
import { hintergrundSchliesst } from './ui/hintergrund'
import { NotizbuchHaekchen } from '../notizbuch'

/**
 * Benutzerverwaltung (AP 2.5) – Rollen, Anmeldekonten, aktiv/inaktiv.
 *
 * Ersetzt `scripts/rolle-setzen.sh` und `scripts/konto-anlegen.sh`. Die Skripte
 * bleiben als Notausgang liegen: wenn niemand mehr an die Oberfläche kommt, ist
 * SSH der einzige Weg zurück.
 *
 * ## Warum die Rolle hier so viel Gewicht hat
 *
 * `steuerer` und `admin` sehen die personenbezogene Auswertung im Leitstand
 * ([E-19](../../../../docs/ENTSCHEIDUNGEN.md)) – wer hier eine Rolle vergibt, gibt
 * Einblick in die Arbeit anderer. Deshalb ist die Verwaltung selbst auf `admin`
 * beschränkt, eine Stufe enger als die Auswertung, und jede Änderung landet im
 * Serverprotokoll.
 *
 * ## Was bewusst fehlt
 *
 * **Löschen.** An einer Person hängen Ereignisse, und die sind die Grundlage
 * jeder Auswertung (E-05). Ausgeschiedene Personen werden deaktiviert – dann
 * fallen sie aus Auswertung und Kapazität, ihre Geschichte bleibt aber lesbar.
 *
 * ## Zusammenführen
 *
 * Von Nils gefordert: „Wir haben Konten aus GitLab, aus NetCon und in Work. Die
 * sollte man kombinieren können, dass es EIN User ist." Auswahl über die
 * Kästchen, dann Ziel bestimmen – der Dialog zeigt vorher, was umzieht
 * ([F-14](../../../../docs/OFFENE-FRAGEN.md)).
 *
 * Gleichnamige Personen werden **vorgeschlagen**, nicht automatisch verschmolzen:
 * Namensgleichheit ist ein starker Hinweis und kein Beweis. Zwei „Michael Schmidt"
 * im Team sind kein Fehler, den ein Abgleich beheben darf.
 */
export function Benutzerverwaltung() {
  const { data: benutzer = [], isLoading, error } = useQuery(benutzerQuery)
  const { data: ich } = useQuery(sitzungQuery)
  const [kontoFuer, setKontoFuer] = useState<Benutzer | null>(null)
  const [neuePersonOffen, setNeuePersonOffen] = useState(false)
  const [abschalten, setAbschalten] = useState<Benutzer | null>(null)
  const [gewaehlt, setGewaehlt] = useState<string[]>([])
  const [mergeZiel, setMergeZiel] = useState<string | null>(null)
  // Getrennt von der Zielwahl: der Dialog soll erst auf „Weiter" kommen, nicht
  // schon beim Antippen des Auswahlfelds.
  const [mergeOffen, setMergeOffen] = useState(false)
  const [meldung, setMeldung] = useState<{ art: 'ok' | 'fehler'; text: string } | null>(null)
  const queryClient = useQueryClient()

  const neuLaden = () => {
    void queryClient.invalidateQueries({ queryKey: ['people'] })
    void queryClient.invalidateQueries({ queryKey: ['analytics'] })
  }

  const rolle = useMutation({
    mutationFn: (v: { personId: string; role: PersonRole }) => trpc.people.rolleSetzen.mutate(v),
    onSuccess: (r, v) => {
      setMeldung({
        art: 'ok',
        text: `${r.displayName}: ${PERSON_ROLE_LABELS[r.vorher]} → ${PERSON_ROLE_LABELS[v.role]}`,
      })
      neuLaden()
    },
    onError: (e: Error) => setMeldung({ art: 'fehler', text: e.message }),
  })

  /**
   * WIP-Limit je Person ([F-15](../../../../docs/OFFENE-FRAGEN.md)).
   *
   * Ab Phase 4 wirkt das Limit – es entscheidet, wer noch etwas ziehen darf. Im
   * echten Bestand liegen alle deutlich darüber (8 und 9 gegen ein Limit von 3),
   * weil der Import zuweist und das Limit auf dem Standardwert steht. Die richtige
   * Zahl kann nur das Team festlegen, deshalb steht sie hier und wird nicht
   * geraten.
   *
   * `onBlur` statt `onChange`: bei jedem Tastendruck zu speichern würde aus „12"
   * erst eine 1 und dann eine 12 machen – und zwischendurch dürfte niemand ziehen.
   */
  /**
   * **„Arbeitet mit"** (21.08.2026, [E-199](../../../docs/ENTSCHEIDUNGEN.md)).
   *
   * Nils: *„Ich muss als Admin gleichzeitig auch Entwickler sein – also dass ich in den
   * Listen stehe."* Steht unter der Rolle und nicht in einer eigenen Spalte: es ist ein
   * Zusatz **zu** der Rolle, und getrennt von ihr wäre nicht zu sehen, worauf es sich
   * bezieht. Sichtbar nur, wo es eine Frage ist – bei Leitung und Administration.
   *
   * `neuLaden()` danach, weil das Häkchen die Entwicklerlisten im ganzen Programm ändert:
   * Bearbeiter-Auswahl, „kommen in Frage", Triage, Kapazität.
   */
  const mitarbeit = useMutation({
    mutationFn: (v: { personId: string; arbeitetMit: boolean }) =>
      trpc.people.arbeitetMitSetzen.mutate(v),
    onSuccess: (r) => {
      setMeldung({
        art: 'ok',
        text: r.arbeitetMit
          ? `${r.displayName} zählt jetzt als Entwickler:in – steht in den Auswahllisten, in der Kapazität und in der Auswertung.`
          : `${r.displayName} zählt nicht mehr als Entwickler:in.`,
      })
      neuLaden()
    },
    onError: (e: Error) => setMeldung({ art: 'fehler', text: e.message }),
  })

  /** Claude darf sich in ihrem Namen verbinden (Z-21). Aus trennt bestehende Verbindungen. */
  const claude = useMutation({
    mutationFn: (v: { personId: string; an: boolean }) => trpc.claude.zugangSetzen.mutate(v),
    onSuccess: (r) => {
      setMeldung({
        art: 'ok',
        text: r.an
          ? `${r.displayName} kann Claude jetzt mit Work verbinden.`
          : `${r.displayName}: Claude-Zugang aus, bestehende Verbindungen getrennt.`,
      })
      neuLaden()
    },
    onError: (e: Error) => setMeldung({ art: 'fehler', text: e.message }),
  })

  /** Claude darf für sie planen (Z-25, E-458) – setzt das Häkchen „🤖 Claude" voraus. */
  const planen = useMutation({
    mutationFn: (v: { personId: string; an: boolean }) => trpc.claude.planungSetzen.mutate(v),
    onSuccess: (r) => {
      setMeldung({
        art: 'ok',
        text: r.an
          ? `${r.displayName}: Claude darf jetzt Vorhaben, Bestand und Menschen lesen und Pläne entwerfen.`
          : `${r.displayName}: Claude plant nicht mehr mit.`,
      })
      neuLaden()
    },
    onError: (e: Error) => setMeldung({ art: 'fehler', text: e.message }),
  })

  const wip = useMutation({
    mutationFn: (v: { personId: string; wipLimit: number }) => trpc.people.wipLimitSetzen.mutate(v),
    onSuccess: (r) => setMeldung({ art: 'ok', text: `${r.displayName}: WIP-Limit ${r.wipLimit}` }),
    onError: (e: Error) => setMeldung({ art: 'fehler', text: e.message }),
  })

  /**
   * **Die Notiz zur Person** (18.08.2026, [E-128](../../../docs/ENTSCHEIDUNGEN.md)).
   *
   * `neuLaden()` danach, damit `defaultValue` beim nächsten Rendern stimmt – ohne das
   * stünde nach einem Zurücksetzen des Feldes wieder der alte Text da.
   */
  const notiz = useMutation({
    mutationFn: (v: { personId: string; notizen: string }) => trpc.people.notizenSetzen.mutate(v),
    onSuccess: (r) => {
      setMeldung({
        art: 'ok',
        text: r.notizen ? `${r.displayName}: Notiz gespeichert` : `${r.displayName}: Notiz geleert`,
      })
      neuLaden()
    },
    onError: (e: Error) => setMeldung({ art: 'fehler', text: e.message }),
  })

  /**
   * **Die Sicht übernehmen** (24.08.2026, [E-214](../../../docs/ENTSCHEIDUNGEN.md)).
   *
   * Danach **ein voller Seitenwechsel** statt `invalidateQueries` – aus demselben Grund wie
   * beim Beenden im `SichtBalken`: der ganze Bestand liegt im Client (Leitplanke 2) und
   * gehört ab jetzt jemand anderem. Ziel ist `/`, weil die Zielperson eine andere
   * Startansicht haben kann als die Administration; `NurErreichbaresAnzeigen` in der
   * `AppShell` schickt sie an die erste, die es für ihre Rolle gibt.
   */
  const sichtAls = useMutation({
    mutationFn: (v: { personId: string }) => trpc.auth.sichtAls.mutate(v),
    onSuccess: () => window.location.assign('/'),
    onError: (e: Error) => setMeldung({ art: 'fehler', text: e.message }),
  })

  const aktiv = useMutation({
    mutationFn: (v: { personId: string; isActive: boolean }) => trpc.people.aktivSetzen.mutate(v),
    onSuccess: (r, v) => {
      setMeldung({
        art: 'ok',
        text: `${r.displayName} ist jetzt ${v.isActive ? 'aktiv' : 'deaktiviert'}.`,
      })
      neuLaden()
    },
    onError: (e: Error) => setMeldung({ art: 'fehler', text: e.message }),
  })

  if (error) {
    return (
      <Card>
        <CardPad>
          <div className="text-[13px] font-semibold">
            Die Benutzerverwaltung ist der Administration vorbehalten.
          </div>
          <p className="mt-1.5 text-xs text-ink2">
            Wer Rollen vergibt, entscheidet darüber, wer personenbezogene Zahlen sieht – deshalb
            liegt sie eine Stufe enger als der Leitstand (E-19). Durchgesetzt wird das im Backend.
          </p>
        </CardPad>
      </Card>
    )
  }

  const leitung = benutzer.filter((b) => b.role === 'steuerer' || b.role === 'admin')

  /** Personen mit gleichem Namen – Kandidaten fürs Zusammenführen (F-14). */
  const nachName = new Map<string, Benutzer[]>()
  for (const person of benutzer) {
    const schluessel = person.displayName.trim().toLowerCase()
    nachName.set(schluessel, [...(nachName.get(schluessel) ?? []), person])
  }
  const doppelte = [...nachName.values()]
    .filter((gruppe) => gruppe.length > 1)
    .map((gruppe) => [gruppe[0]?.displayName ?? '', gruppe] as const)

  const gewaehlteBenutzer = benutzer.filter((b) => gewaehlt.includes(b.id))
  const ziel = gewaehlteBenutzer.find((b) => b.id === mergeZiel)

  return (
    <>
      <Card>
        <CardPad>
          <p className="text-xs text-ink2">
            Die Auswertung im Leitstand sehen nur <strong>Steuerung</strong> und{' '}
            <strong>Administration</strong> – derzeit{' '}
            {leitung.length === 0 ? (
              <strong className="text-pool-sofort">niemand</strong>
            ) : (
              <strong>{leitung.map((b) => b.displayName).join(', ')}</strong>
            )}
            . Importierte Personen sind seit dem 18.08.2026 <strong>Gast</strong>: aus GitLab lässt
            sich nicht ablesen, wer entwickelt und wer nur einmal ein Ticket gemeldet hat – und zu
            wenig Rechte sind besser als zu viele. Wer entwickelt, wird hier hochgestuft.
          </p>

          <GastVorschlaege beiMeldung={setMeldung} />

          {/**
           * **Wer in keinem Quellsystem vorkommt, steht nicht in dieser Liste** (Phase 9).
           *
           * Sie entsteht vollständig aus GitLab und NetCon. Ein Abteilungsleiter, der nie ein
           * Issue angelegt hat, fehlt darin – und genau er soll Mit-Owner werden. Ohne diesen
           * Knopf gäbe es die Rolle, aber niemanden, dem man sie geben könnte.
           */}
          <div className="mt-3">
            <Button size="sm" onClick={() => setNeuePersonOffen(true)}>
              + Person anlegen
            </Button>
            <span className="ml-2 text-[11.5px] text-muted">
              Für Menschen aus den Fachbereichen – die haben kein GitLab-Konto.
            </span>
          </div>

          {meldung && (
            <p
              className={`mt-3 rounded-lg px-2.5 py-2 text-[12.5px] ${
                meldung.art === 'ok' ? 'bg-[#eef7ee] text-[#1d6b34]' : 'bg-[#fdeaea] text-[#b02a2a]'
              }`}
              role="status"
            >
              {meldung.text}
            </p>
          )}

          {isLoading && <p className="mt-3 text-[13px] text-ink2">Lädt …</p>}

          {/*
            Gleichnamige Personen vorschlagen. Sie entstehen, weil GitLab und
            NetCon keine E-Mail herausgeben und der Importer die Person deshalb
            nicht mit einem vorhandenen Konto verbinden kann (F-14). Nur ein
            Hinweis – Namensgleichheit ist kein Beweis.
          */}
          {doppelte.map(([name, gruppe]) => (
            <div
              key={name}
              className="mt-3 flex flex-wrap items-center gap-2 rounded-lg bg-[#fdf3dc] px-2.5 py-2 text-[12.5px] text-[#8a6100]"
            >
              <span>
                <strong>{gruppe.length} Personen</strong> heißen „{name}" –
                {gruppe.some((p) => p.canLogin)
                  ? ' eine davon hat ein Anmeldekonto.'
                  : ' keine davon hat ein Anmeldekonto.'}{' '}
                Dieselbe Person aus mehreren Quellen?
              </span>
              <Button
                variant="ghost"
                size="sm"
                className="ml-auto"
                onClick={() => {
                  setMeldung(null)
                  setGewaehlt(gruppe.map((p) => p.id))
                  // Ziel ist die Person mit Anmeldekonto – sonst ginge der
                  // Zugang verloren, und das Backend lehnt es ohnehin ab.
                  setMergeZiel((gruppe.find((p) => p.canLogin) ?? gruppe[0])?.id ?? null)
                  setMergeOffen(true)
                }}
              >
                Zusammenführen …
              </Button>
            </div>
          ))}

          {gewaehlt.length >= 1 && (
            <WeltZuordnung
              personen={gewaehlteBenutzer}
              beiMeldung={setMeldung}
              fertig={() => setGewaehlt([])}
            />
          )}

          {gewaehlt.length >= 2 && (
            <div className="mt-3 flex flex-wrap items-center gap-2 rounded-lg bg-accent-soft px-2.5 py-2 text-[12.5px] text-accent-deep">
              <span>
                <strong>{gewaehlt.length} Personen gewählt.</strong> Welche bleibt? Alles andere
                zieht dorthin um.
              </span>
              <select
                value={mergeZiel ?? ''}
                onChange={(e) => setMergeZiel(e.target.value || null)}
                aria-label="Person, die bleibt"
                className="cursor-pointer rounded-lg border border-line2 bg-surface px-2 py-1 text-[12px] text-ink"
              >
                <option value="">Ziel wählen …</option>
                {gewaehlteBenutzer.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.displayName}
                    {p.canLogin ? ' (mit Anmeldung)' : ''}
                    {p.email ? ` · ${p.email}` : ''}
                  </option>
                ))}
              </select>
              <Button
                variant="primary"
                size="sm"
                disabled={!mergeZiel}
                onClick={() => {
                  setMeldung(null)
                  setMergeOffen(true)
                }}
              >
                Weiter
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setGewaehlt([])
                  setMergeZiel(null)
                }}
              >
                Auswahl aufheben
              </Button>
            </div>
          )}

          {benutzer.length > 0 && (
            <div className="mt-3 overflow-x-auto">
              <table className="min-w-[560px] w-full border-collapse text-[12.5px]">
                <thead>
                  <tr>
                    {/* Neun Spalten, neun Überschriften – „WIP" fehlte, wodurch alles
                        dahinter um eine Spalte verschoben beschriftet war. Seit dem
                        26.08.2026 zehn: die Workspaces (Z-07). */}
                    {[
                      '',
                      'Person',
                      'Herkunft',
                      'Offen',
                      'Ereignisse',
                      'WIP',
                      'Anmeldung',
                      'Rolle',
                      'Workspaces',
                      '',
                    ].map((k, i) => (
                      <th
                        key={k || i}
                        className={`border-b border-line2 px-2 py-2 text-[10.5px] font-semibold tracking-[0.5px] text-muted uppercase ${
                          i === 3 || i === 4 || i === 5 ? 'text-right' : 'text-left'
                        }`}
                      >
                        {i === 0 ? (
                          /**
                           * **Alle auf einmal** (16.09.2026, E-355) – für die Sammel-Zuordnung
                           * zu einer Arbeitswelt. Angehakt werden nur die aktiven: eine
                           * deaktivierte Person in eine Welt zu setzen wäre eine Zuteilung an
                           * jemanden, der nicht mehr da ist.
                           */
                          <input
                            type="checkbox"
                            checked={
                              benutzer.some((b) => b.isActive) &&
                              benutzer
                                .filter((b) => b.isActive)
                                .every((b) => gewaehlt.includes(b.id))
                            }
                            onChange={(e) =>
                              setGewaehlt(
                                e.target.checked
                                  ? benutzer.filter((b) => b.isActive).map((b) => b.id)
                                  : [],
                              )
                            }
                            aria-label="Alle aktiven Personen wählen"
                            className="cursor-pointer"
                          />
                        ) : (
                          k
                        )}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {benutzer.map((person) => (
                    <tr key={person.id} className={person.isActive ? '' : 'opacity-55'}>
                      <td className="border-b border-line px-2 py-2">
                        <input
                          type="checkbox"
                          checked={gewaehlt.includes(person.id)}
                          onChange={(e) =>
                            setGewaehlt((v) =>
                              e.target.checked
                                ? [...v, person.id]
                                : v.filter((id) => id !== person.id),
                            )
                          }
                          aria-label={`${person.displayName} wählen`}
                          className="cursor-pointer"
                        />
                      </td>
                      <td className="border-b border-line px-2 py-2">
                        <div className="font-semibold">{person.displayName}</div>
                        <div className="text-[11px] text-muted">
                          {person.email ?? 'keine E-Mail'}
                          {!person.isActive && ' · deaktiviert'}
                        </div>
                        {/**
                         * **Wofür jemand steht** (18.08.2026, [E-128](../../../docs/ENTSCHEIDUNGEN.md)).
                         *
                         * Steht **unter dem Namen** und nicht in einer eigenen Spalte: es ist
                         * eine Aussage über die Person, kein Messwert wie „Offen" oder
                         * „Ereignisse". In einer Spalte am rechten Rand läse es niemand.
                         *
                         * Gespeichert beim Verlassen des Feldes, wie das WIP-Limit daneben –
                         * kein Speichern-Knopf für ein Feld, das man im Vorbeigehen ändert.
                         *
                         * ## Warum ein wachsendes Textfeld und keine Zeile (20.08.2026)
                         *
                         * Es war eine einzeilige `<input>`, gebaut für **einen Satz**. Seit
                         * „übernehmen & bearbeiten" ein vierzeiliges Profil hineinschreibt, war
                         * es unbedienbar – Nils, mit einem Bild davon: *„das kann man hier
                         * schlecht bearbeiten in der einzeiligen Textzeile."*
                         *
                         * Ein Popup wäre die andere Lösung gewesen und die schlechtere: der Text
                         * steht an der Person, wird im Vorbeigehen geändert, und ein Dialog dafür
                         * kostet zwei Klicks für eine Korrektur von drei Wörtern. Das Feld wächst
                         * jetzt mit dem Inhalt, von einer Zeile bis zu zehn.
                         */}
                        <textarea
                          key={person.notizen ?? ''}
                          defaultValue={person.notizen ?? ''}
                          disabled={notiz.isPending}
                          rows={Math.min(
                            10,
                            Math.max(1, (person.notizen ?? '').split('\n').length),
                          )}
                          placeholder="wofür steht die Person? (z. B. Portierungen, IOR)"
                          onBlur={(e) => {
                            const wert = e.target.value.trim()
                            if (wert === (person.notizen ?? '')) return
                            setMeldung(null)
                            notiz.mutate({ personId: person.id, notizen: wert })
                          }}
                          aria-label={`Notiz zu ${person.displayName}`}
                          className="mt-1 w-full min-w-[260px] resize-y rounded-lg border border-line2 bg-surface px-1.5 py-1 text-[11.5px] leading-snug placeholder:text-muted"
                        />

                        {/**
                         * **Das abgeleitete Profil darunter** (20.08.2026,
                         * [E-177](../../../docs/ENTSCHEIDUNGEN.md)).
                         *
                         * Blass und nicht bearbeitbar – es ist eine **Auskunft**, keine Eingabe.
                         * Wer es korrigieren will, drückt „übernehmen": dann wandert der Text
                         * nach oben ins eigene Feld, und ab da gehört er ihm. Ein neuer Lauf
                         * kann ihn dort nicht mehr wegräumen.
                         *
                         * Der Stand steht dabei, weil ein Profil von vor einem Jahr als alt
                         * erkennbar sein muss – sonst zementiert es, was jemand einmal gemacht
                         * hat ([E-158](../../../docs/ENTSCHEIDUNGEN.md)).
                         */}
                        {person.profilKi && (
                          <div className="mt-1.5 rounded-lg border border-line2 bg-[#faf9f7] px-2 py-1.5">
                            <div className="flex items-baseline gap-2">
                              <span className="text-[10px] font-semibold uppercase tracking-wide text-muted">
                                KI-Profil
                              </span>
                              <span className="text-[10.5px] text-muted">
                                {person.profilAusAufgaben ?? 0} Aufgaben
                                {person.profilStand
                                  ? ` · Stand ${new Date(person.profilStand).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' })}`
                                  : ''}
                              </span>
                              {/**
                               * **Zweimal drücken darf nicht zweimal anhängen** (20.08.2026).
                               *
                               * Der erste Bau hängte den Profiltext bedingungslos an die Notiz –
                               * ein zweiter Klick schrieb ihn ein zweites Mal darunter. Ein Knopf,
                               * der beim wiederholten Drücken etwas anderes tut als beim ersten,
                               * ist eine Falle; hier sagt er stattdessen, dass es schon geschehen
                               * ist.
                               *
                               * Verglichen wird auf **Enthaltensein**, nicht auf Gleichheit: wer
                               * den übernommenen Text kürzt, soll ihn erneut holen können, wer ihn
                               * unverändert stehen lässt, nicht.
                               */}
                              {(() => {
                                const schonDrin = (person.notizen ?? '').includes(
                                  person.profilKi.trim(),
                                )
                                return (
                                  <button
                                    type="button"
                                    disabled={notiz.isPending || schonDrin}
                                    onClick={() => {
                                      setMeldung(null)
                                      notiz.mutate({
                                        personId: person.id,
                                        /** Vorhandenes bleibt stehen – übernehmen heißt ergänzen, nicht ersetzen. */
                                        notizen: [person.notizen, person.profilKi]
                                          .filter(Boolean)
                                          .join('\n'),
                                      })
                                    }}
                                    className="ml-auto cursor-pointer rounded border border-line2 px-1.5 py-px text-[10.5px] font-semibold text-accent-deep hover:bg-white disabled:cursor-default disabled:border-transparent disabled:text-muted"
                                    title={
                                      schonDrin
                                        ? 'Steht schon oben im eigenen Notizfeld'
                                        : 'Text ins eigene Notizfeld übernehmen – dort kann kein Lauf ihn mehr überschreiben'
                                    }
                                  >
                                    {schonDrin ? '✓ übernommen' : 'übernehmen & bearbeiten'}
                                  </button>
                                )
                              })()}
                            </div>
                            <p className="mt-0.5 whitespace-pre-line text-[11px] leading-snug text-ink2">
                              {person.profilKi}
                            </p>
                          </div>
                        )}
                      </td>

                      <td className="border-b border-line px-2 py-2 text-[11px] text-muted">
                        {person.quellen.length === 0
                          ? 'in Work angelegt'
                          : person.quellen
                              .map(
                                // `SOURCE_LABELS`, nicht `TASK_SOURCE_LABELS`:
                                // hier steht die Herkunft einer *Person*, und dazu
                                // gehört das Intranet, das keine Aufgaben liefert.
                                (q) =>
                                  `${SOURCE_LABELS[q.sourceId] ?? q.sourceId}${
                                    q.username ? ` (${q.username})` : ''
                                  }`,
                              )
                              .join(', ')}
                      </td>

                      <td className="tabular border-b border-line px-2 py-2 text-right text-[11.5px]">
                        {person.offeneAufgaben === 0 ? (
                          <span className="text-muted">0</span>
                        ) : (
                          /* Bei Deaktivierten hervorgehoben: das ist Arbeit, die
                             niemand macht. */
                          <span
                            className={
                              person.isActive ? 'text-ink2' : 'font-semibold text-[#8a6100]'
                            }
                          >
                            {person.offeneAufgaben}
                          </span>
                        )}
                      </td>

                      <td className="tabular border-b border-line px-2 py-2 text-right text-[11.5px] text-ink2">
                        {person.ereignisse === 0 ? (
                          <span className="text-muted">0</span>
                        ) : (
                          person.ereignisse.toLocaleString('de-DE')
                        )}
                      </td>

                      <td className="border-b border-line px-2 py-2 text-right">
                        <input
                          type="number"
                          min={0}
                          max={50}
                          defaultValue={person.wipLimit}
                          disabled={wip.isPending}
                          onBlur={(e) => {
                            const wert = Number(e.target.value)
                            if (!Number.isInteger(wert) || wert < 0 || wert > 50) {
                              e.target.value = String(person.wipLimit)
                              return
                            }
                            if (wert === person.wipLimit) return
                            setMeldung(null)
                            wip.mutate({ personId: person.id, wipLimit: wert })
                          }}
                          aria-label={`WIP-Limit von ${person.displayName}`}
                          className="tabular w-[52px] rounded-lg border border-line2 bg-surface px-1.5 py-1 text-right text-[12px]"
                        />
                      </td>

                      <td className="border-b border-line px-2 py-2">
                        {person.canLogin ? (
                          <span className="text-[11px] font-semibold text-[#0d7a52]">
                            Konto vorhanden
                          </span>
                        ) : (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setMeldung(null)
                              setKontoFuer(person)
                            }}
                          >
                            Konto anlegen
                          </Button>
                        )}
                      </td>

                      <td className="border-b border-line px-2 py-2">
                        <select
                          value={person.role}
                          disabled={rolle.isPending}
                          onChange={(e) => {
                            setMeldung(null)
                            rolle.mutate({
                              personId: person.id,
                              role: e.target.value as PersonRole,
                            })
                          }}
                          aria-label={`Rolle von ${person.displayName}`}
                          className="cursor-pointer rounded-lg border border-line2 bg-surface px-2 py-1 text-[12px]"
                        >
                          {PERSON_ROLES.map((r) => (
                            <option key={r} value={r}>
                              {PERSON_ROLE_LABELS[r]}
                            </option>
                          ))}
                        </select>

                        {/**
                         * Nur bei Leitung und Administration: bei `entwickler` ist die Frage
                         * beantwortet, bei Mit-Owner und Gast wäre das Häkchen die Umgehung
                         * einer Rollenentscheidung. Der Server prüft dasselbe noch einmal.
                         */}
                        {kannMitarbeiten(person.role) && (
                          <label className="mt-1 flex cursor-pointer items-center gap-1.5 text-[11px] text-ink2">
                            <input
                              type="checkbox"
                              checked={person.arbeitetMit}
                              disabled={mitarbeit.isPending}
                              onChange={(e) => {
                                setMeldung(null)
                                mitarbeit.mutate({
                                  personId: person.id,
                                  arbeitetMit: e.target.checked,
                                })
                              }}
                              className="cursor-pointer"
                            />
                            arbeitet mit
                          </label>
                        )}
                        {person.canLogin && (
                          <label className="mt-1 flex cursor-pointer items-center gap-1.5 text-[11px] text-ink2">
                            <input
                              type="checkbox"
                              checked={person.claudeZugang}
                              disabled={claude.isPending}
                              onChange={(e) => {
                                setMeldung(null)
                                claude.mutate({ personId: person.id, an: e.target.checked })
                              }}
                              className="cursor-pointer"
                            />
                            🤖 Claude
                          </label>
                        )}
                        {person.canLogin && person.claudeZugang && (
                          <label
                            className="mt-1 flex cursor-pointer items-center gap-1.5 text-[11px] text-ink2"
                            title="Claude darf Vorhaben, Bestand und Menschen lesen und Planentwürfe anlegen (Z-25)"
                          >
                            <input
                              type="checkbox"
                              checked={person.claudePlanung}
                              disabled={planen.isPending}
                              onChange={(e) => {
                                setMeldung(null)
                                planen.mutate({ personId: person.id, an: e.target.checked })
                              }}
                              className="cursor-pointer"
                            />
                            ✦ plant mit
                          </label>
                        )}
                        {person.canLogin && <NotizbuchHaekchen personId={person.id} />}
                      </td>

                      {/**
                       * **Zu welchen Arbeitswelten gehört diese Person** (Nils, 26.08.2026:
                       * *„die Entwickler-Rolle muss zusätzlich Workspaces zugeteilt werden bei
                       * den Benutzern"*).
                       *
                       * Die Zuteilung gab es schon – aber nur von der **anderen** Seite, unter
                       * Einstellungen → Workspaces → Mitglieder. Wer jemanden neu anlegt, ist
                       * hier und denkt in Personen, nicht in Arbeitswelten. Beide Wege
                       * schreiben dieselbe Tabelle.
                       */}
                      <td className="border-b border-line px-2 py-2">
                        <WorkspaceChips person={person} />
                      </td>

                      <td className="border-b border-line px-2 py-2 text-right whitespace-nowrap">
                        {/**
                         * **„Anmelden als"** (Nils, 24.08.2026,
                         * [E-214](../../../docs/ENTSCHEIDUNGEN.md)) – genauer: *ansehen als*,
                         * denn geschrieben wird in der Sicht nichts.
                         *
                         * **Ausdrücklich auch bei Personen ohne Anmeldekonto.** Die erste
                         * Fassung verlangte `canLogin`, und damit fehlte der Knopf bei allen
                         * sechs Entwicklern: im echten Bestand haben genau zwei Personen ein
                         * Work-Konto. Der Zweck ist die Vorschau **vor** der Kontovergabe –
                         * *was wird die sehen, wenn sie sich anmeldet?* Begründung in
                         * `personNachId`.
                         */}
                        {person.isActive && person.id !== ich?.id && (
                          <Button
                            variant="ghost"
                            size="sm"
                            disabled={sichtAls.isPending}
                            title={`Work mit den Augen von ${person.displayName} ansehen`}
                            onClick={() => {
                              setMeldung(null)
                              sichtAls.mutate({ personId: person.id })
                            }}
                          >
                            Ansehen als
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          disabled={aktiv.isPending}
                          onClick={() => {
                            setMeldung(null)
                            // Rückfrage nur, wenn tatsächlich etwas dranhängt –
                            // ein „Sind Sie sicher?" bei jedem Klick liest
                            // niemand mehr, wenn es darauf ankommt.
                            if (person.isActive && person.offeneAufgaben > 0) {
                              setAbschalten(person)
                              return
                            }
                            aktiv.mutate({ personId: person.id, isActive: !person.isActive })
                          }}
                        >
                          {person.isActive ? 'Deaktivieren' : 'Aktivieren'}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <p className="mt-3 text-[11px] text-muted">
            Personen werden <strong>nicht gelöscht</strong> – an ihnen hängen Ereignisse, und die
            sind die Grundlage jeder Auswertung. Das <strong>WIP-Limit</strong> wirkt ab Phase 4: es
            entscheidet, wer noch aus einem Pool ziehen darf. <strong>0</strong> heißt „zieht gerade
            nichts" – Sofort-Aufgaben gehen trotzdem. <strong>Arbeitet mit</strong> gibt es bei
            Leitung und Administration: die Person zählt dann überall als Entwickler:in – in der
            Bearbeiter-Auswahl, in „kommen in Frage", in der Triage, in Kapazität und Auswertung –,
            behält aber die Rechte ihrer Rolle. Deaktiviert heißt: fällt aus Auswertung und
            Kapazität, Geschichte bleibt lesbar. Selbstregistrierung gibt es weiterhin nicht.
          </p>
        </CardPad>
      </Card>

      {/**
       * Intranet-Zuordnung (Z-01) – eigene Karte direkt darunter.
       *
       * Sie gehört hierher und nicht in einen eigenen Menüpunkt: es ist derselbe
       * Vorgang wie „welches GitLab-Konto ist diese Person", nur mit einer dritten
       * Quelle. Wer Personen pflegt, pflegt sie an einer Stelle.
       */}
      <div className="mt-[18px]">
        <IntranetZuordnung />
      </div>

      {mergeOffen && ziel && gewaehlt.length >= 2 && (
        <PersonenZusammenfuehren
          ziel={ziel}
          quellen={gewaehlteBenutzer.filter((b) => b.id !== ziel.id)}
          schliessen={() => setMergeOffen(false)}
          fertig={(text) => {
            setMergeOffen(false)
            setMergeZiel(null)
            setGewaehlt([])
            setMeldung({ art: 'ok', text })
            neuLaden()
          }}
        />
      )}

      {abschalten && (
        <Abschaltfrage
          person={abschalten}
          abbrechen={() => setAbschalten(null)}
          bestaetigen={() => {
            const person = abschalten
            setAbschalten(null)
            aktiv.mutate({ personId: person.id, isActive: false })
          }}
        />
      )}

      {neuePersonOffen && (
        <NeuePerson
          schliessen={() => setNeuePersonOffen(false)}
          fertig={(text) => {
            setNeuePersonOffen(false)
            setMeldung({ art: 'ok', text })
            neuLaden()
          }}
        />
      )}

      {kontoFuer && (
        <KontoAnlegen
          person={kontoFuer}
          schliessen={() => setKontoFuer(null)}
          fertig={(text) => {
            setKontoFuer(null)
            setMeldung({ art: 'ok', text })
            neuLaden()
          }}
        />
      )}
    </>
  )
}

/**
 * Anmeldekonto anlegen.
 *
 * Das Passwort wird hier gesetzt und nirgends angezeigt oder protokolliert. Es
 * geht über Better-Auth, damit derselbe Hash entsteht, den die Anmeldung
 * erwartet – ein eigenes Passwortverfahren gibt es im Code nicht.
 */
/**
 * **„Diese würde ich auf Gast herabstufen"** (18.08.2026,
 * [E-130](../../../../docs/ENTSCHEIDUNGEN.md)).
 *
 * Der Bestand trug über dreißig „Entwickler:innen", weil der Importeur bis dahin jeden Namen
 * so anlegte – Melder aus Kundentickets, „Ghost User", Testkonten. In der Auswahl „kommen in
 * Frage" standen sie alle.
 *
 * ## Warum eine Liste zum Abhaken und kein Knopf „aufräumen"
 *
 * Weil die Merkmale (kein Konto, keine offene Aufgabe, seit einem Jahr keine eigene Spur)
 * **fast** immer stimmen – und beim Rest säße jemand plötzlich als Gast in seiner eigenen
 * Anwendung. Ein Vorschlag mit Begründung kostet zwei Minuten und trifft niemanden falsch.
 *
 * Die Karte verschwindet, sobald nichts mehr vorzuschlagen ist. Eine dauerhafte Fläche mit
 * „nichts zu tun" ist eine Fläche, über die man hinwegliest.
 */
function GastVorschlaege({
  beiMeldung,
}: {
  beiMeldung: (m: { art: 'ok' | 'fehler'; text: string }) => void
}) {
  const queryClient = useQueryClient()
  const { data: vorschlaege = [], error } = useQuery(gastVorschlaegeQuery)
  const [abgewaehlt, setAbgewaehlt] = useState<string[]>([])
  const [offen, setOffen] = useState(false)

  const herabstufen = useMutation({
    mutationFn: (personIds: string[]) =>
      trpc.people.rolleSammelSetzen.mutate({ personIds, role: 'gast' }),
    onSuccess: (r) => {
      beiMeldung({
        art: 'ok',
        text: `${r.geaendert} ${r.geaendert === 1 ? 'Person' : 'Personen'} auf Gast herabgestuft.${
          r.uebersprungen > 0 ? ` ${r.uebersprungen} übersprungen (Administration).` : ''
        }`,
      })
      void queryClient.invalidateQueries({ queryKey: ['people'] })
    },
    onError: (e: Error) => beiMeldung({ art: 'fehler', text: e.message }),
  })

  if (error || vorschlaege.length === 0) return null

  const gewaehlt = vorschlaege.filter((v) => !abgewaehlt.includes(v.id))

  return (
    <div className="mt-3 rounded-lg bg-[#fdf3dc] px-2.5 py-2 text-[12.5px] text-[#8a6100]">
      <div className="flex flex-wrap items-center gap-2">
        <span>
          <strong>{vorschlaege.length} Personen</strong> sind als Team gekennzeichnet, arbeiten aber
          nicht in Work: kein Anmeldekonto, keine offene Aufgabe, seit einem Jahr keine eigene Spur.
          Als <strong>Gast</strong> bleiben sie sichtbar und ansprechbar – nur nicht mehr in den
          Entwickler-Auswahlen.
        </span>
        <button
          type="button"
          onClick={() => setOffen((v) => !v)}
          className="cursor-pointer underline"
        >
          {offen ? 'zuklappen' : 'ansehen'}
        </button>
      </div>

      {offen && (
        <>
          <div className="mt-2 flex max-h-[220px] flex-col gap-0.5 overflow-y-auto">
            {vorschlaege.map((v) => (
              <label key={v.id} className="flex cursor-pointer items-baseline gap-2">
                <input
                  type="checkbox"
                  checked={!abgewaehlt.includes(v.id)}
                  onChange={() =>
                    setAbgewaehlt((alt) =>
                      alt.includes(v.id) ? alt.filter((id) => id !== v.id) : [...alt, v.id],
                    )
                  }
                  className="cursor-pointer"
                />
                <span className="font-semibold">{v.displayName}</span>
                <span className="text-[11px]">
                  {PERSON_ROLE_LABELS[v.role]}
                  {v.zuletztSelbst
                    ? ` · zuletzt selbst aktiv ${new Date(v.zuletztSelbst).toLocaleDateString('de-DE')}`
                    : ' · nie selbst aktiv'}
                </span>
              </label>
            ))}
          </div>

          <div className="mt-2 flex items-center gap-2">
            <Button
              variant="primary"
              size="sm"
              disabled={gewaehlt.length === 0 || herabstufen.isPending}
              onClick={() => herabstufen.mutate(gewaehlt.map((v) => v.id))}
            >
              {herabstufen.isPending ? 'Stuft herab …' : `${gewaehlt.length} auf Gast herabstufen`}
            </Button>
            <span className="text-[11px]">
              Umkehrbar: die Rolle steht in jeder Zeile der Liste unten.
            </span>
          </div>
        </>
      )}
    </div>
  )
}

function KontoAnlegen({
  person,
  schliessen,
  fertig,
}: {
  person: Benutzer
  schliessen: () => void
  fertig: (text: string) => void
}) {
  const [email, setEmail] = useState(person.email ?? '')
  const [passwort, setPasswort] = useState('')
  const [wiederholung, setWiederholung] = useState('')
  const [fehler, setFehler] = useState<string | null>(null)

  const anlegen = useMutation({
    mutationFn: (v: { personId: string; email: string; passwort: string }) =>
      trpc.people.kontoAnlegen.mutate(v),
    onSuccess: (r) => fertig(`Anmeldekonto für ${r.displayName} angelegt.`),
    onError: (e: Error) => setFehler(e.message),
  })

  function absenden(e: React.FormEvent) {
    e.preventDefault()
    setFehler(null)
    if (passwort !== wiederholung) return setFehler('Die Passwörter stimmen nicht überein.')
    if (passwort.length < 12) return setFehler('Das Passwort braucht mindestens 12 Zeichen.')
    anlegen.mutate({ personId: person.id, email: email.trim(), passwort })
  }

  return (
    <div
      className="fixed inset-x-0 bottom-0 unter-leiste z-50 flex items-start justify-center bg-[rgb(23_22_20/0.38)] p-3 pt-[4vh] sm:p-6 sm:pt-[12vh]"
      {...hintergrundSchliesst(schliessen)}
      role="dialog"
      aria-modal="true"
      aria-label={`Anmeldekonto für ${person.displayName}`}
    >
      <form
        onSubmit={absenden}
        className="w-full min-w-0 max-w-[460px] rounded-card border border-line bg-surface p-[18px] shadow-card"
      >
        <h3 className="text-sm font-bold">Anmeldekonto für {person.displayName}</h3>
        <p className="mt-1 text-[11.5px] text-muted">
          Die E-Mail ist der Anmeldename. Das Passwort wird nirgends angezeigt – gib es der Person
          weiter, sie kann es danach selbst ändern.
        </p>

        <label className="mt-3 block text-[11.5px] font-semibold text-ink2">
          E-Mail
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="off"
            className="mt-1 w-full rounded-[9px] border border-line2 bg-surface px-[11px] py-2 text-[13px] font-normal"
          />
        </label>

        <label className="mt-2.5 block text-[11.5px] font-semibold text-ink2">
          Passwort (mindestens 12 Zeichen)
          <input
            type="password"
            required
            value={passwort}
            onChange={(e) => setPasswort(e.target.value)}
            autoComplete="new-password"
            className="mt-1 w-full rounded-[9px] border border-line2 bg-surface px-[11px] py-2 text-[13px] font-normal"
          />
        </label>

        <label className="mt-2.5 block text-[11.5px] font-semibold text-ink2">
          Passwort wiederholen
          <input
            type="password"
            required
            value={wiederholung}
            onChange={(e) => setWiederholung(e.target.value)}
            autoComplete="new-password"
            className="mt-1 w-full rounded-[9px] border border-line2 bg-surface px-[11px] py-2 text-[13px] font-normal"
          />
        </label>

        {fehler && (
          <p className="mt-2.5 rounded-lg bg-[#fdeaea] px-2.5 py-2 text-[12.5px] text-[#b02a2a]">
            {fehler}
          </p>
        )}

        <div className="mt-3.5 flex gap-2">
          <Button type="submit" variant="primary" size="sm" disabled={anlegen.isPending}>
            {anlegen.isPending ? 'Legt an …' : 'Konto anlegen'}
          </Button>
          <Button type="button" variant="ghost" size="sm" onClick={schliessen}>
            Abbrechen
          </Button>
        </div>
      </form>
    </div>
  )
}

/**
 * **Eine Person von Hand anlegen** (Phase 9).
 *
 * Für Menschen aus den Fachbereichen: sie kommen in keinem Quellsystem vor, also legt sie
 * niemand automatisch an.
 *
 * ## Warum die Rolle hier gewählt wird und nicht hinterher
 *
 * Weil sie der Grund ist, aus dem jemand diesen Dialog öffnet. Die Person erst als
 * Entwickler:in anzulegen und die Rolle danach in der Tabelle zu ändern wäre ein zweiter
 * Schritt, den man vergisst – und dann hat ein Abteilungsleiter für einen Moment Zugriff auf
 * Pools, Eingang und Leitstand.
 */
function NeuePerson({
  schliessen,
  fertig,
}: {
  schliessen: () => void
  fertig: (text: string) => void
}) {
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [role, setRole] = useState<PersonRole>('mit_owner')
  const [passwort, setPasswort] = useState('')
  const [wiederholung, setWiederholung] = useState('')
  const [fehler, setFehler] = useState<string | null>(null)

  const anlegen = useMutation({
    mutationFn: (v: { displayName: string; email: string; role: PersonRole; passwort: string }) =>
      trpc.people.personAnlegen.mutate(v),
    onSuccess: (r) =>
      fertig(
        r.gleichnamigeVorhanden
          ? `${r.displayName} angelegt. Achtung: es gibt bereits jemanden mit diesem Namen – falls es dieselbe Person ist, führe beide zusammen.`
          : `${r.displayName} angelegt.`,
      ),
    onError: (e: Error) => setFehler(e.message),
  })

  function absenden(e: React.FormEvent) {
    e.preventDefault()
    setFehler(null)
    if (passwort !== wiederholung) return setFehler('Die Passwörter stimmen nicht überein.')
    if (passwort.length < 12) return setFehler('Das Passwort braucht mindestens 12 Zeichen.')
    anlegen.mutate({ displayName: name.trim(), email: email.trim(), role, passwort })
  }

  return (
    <div
      className="fixed inset-x-0 bottom-0 unter-leiste z-50 flex items-start justify-center bg-[rgb(23_22_20/0.38)] p-3 pt-[4vh] sm:p-6 sm:pt-[12vh]"
      {...hintergrundSchliesst(schliessen)}
      role="dialog"
      aria-modal="true"
      aria-label="Person anlegen"
    >
      <form
        onSubmit={absenden}
        className="w-full min-w-0 max-w-[460px] rounded-card border border-line bg-surface p-[18px] shadow-card"
      >
        <h3 className="text-sm font-bold">Person anlegen</h3>
        <p className="mt-1 text-[11.5px] text-muted">
          Für jemanden, der in GitLab und NetCon nicht vorkommt. Person und Anmeldekonto entstehen
          in einem Schritt – ohne Konto könnte sie sich nicht anmelden, und dafür ist sie hier.
        </p>

        <label className="mt-3 block text-[11.5px] font-semibold text-ink2">
          Name
          <input
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="mt-1 w-full rounded-[9px] border border-line2 bg-surface px-[11px] py-2 text-[13px] font-normal"
          />
        </label>

        <label className="mt-2.5 block text-[11.5px] font-semibold text-ink2">
          E-Mail (zugleich Anmeldename)
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="off"
            className="mt-1 w-full rounded-[9px] border border-line2 bg-surface px-[11px] py-2 text-[13px] font-normal"
          />
        </label>

        <label className="mt-2.5 block text-[11.5px] font-semibold text-ink2">
          Rolle
          <select
            value={role}
            onChange={(e) => setRole(e.target.value as PersonRole)}
            className="mt-1 w-full cursor-pointer rounded-[9px] border border-line2 bg-surface px-[11px] py-2 text-[13px] font-normal"
          >
            {PERSON_ROLES.map((r) => (
              <option key={r} value={r}>
                {PERSON_ROLE_LABELS[r]}
              </option>
            ))}
          </select>
        </label>
        {role === 'mit_owner' && (
          <p className="mt-1 text-[11px] text-muted">
            Sieht nur die Vorhaben, die er betreut, und was er selbst gemeldet hat. Darf einstellen,
            eine Priorität vorschlagen und abnehmen – nicht ziehen, nicht zuweisen.
          </p>
        )}

        <label className="mt-2.5 block text-[11.5px] font-semibold text-ink2">
          Passwort (mindestens 12 Zeichen)
          <input
            type="password"
            required
            value={passwort}
            onChange={(e) => setPasswort(e.target.value)}
            autoComplete="new-password"
            className="mt-1 w-full rounded-[9px] border border-line2 bg-surface px-[11px] py-2 text-[13px] font-normal"
          />
        </label>

        <label className="mt-2.5 block text-[11.5px] font-semibold text-ink2">
          Passwort wiederholen
          <input
            type="password"
            required
            value={wiederholung}
            onChange={(e) => setWiederholung(e.target.value)}
            autoComplete="new-password"
            className="mt-1 w-full rounded-[9px] border border-line2 bg-surface px-[11px] py-2 text-[13px] font-normal"
          />
        </label>

        {fehler && (
          <p className="mt-2.5 rounded-lg bg-[#fdeaea] px-2.5 py-2 text-[12.5px] text-[#b02a2a]">
            {fehler}
          </p>
        )}

        <div className="mt-3.5 flex gap-2">
          <Button type="submit" variant="primary" size="sm" disabled={anlegen.isPending}>
            {anlegen.isPending ? 'Legt an …' : 'Anlegen'}
          </Button>
          <Button type="button" variant="ghost" size="sm" onClick={schliessen}>
            Abbrechen
          </Button>
        </div>
      </form>
    </div>
  )
}

/**
 * Rückfrage vor dem Deaktivieren einer Person, an der noch offene Aufgaben hängen.
 *
 * Sie erscheint **nur dann** – eine Rückfrage bei jedem Klick wird zur Gewohnheit
 * und damit wirkungslos.
 *
 * Was sie sagen muss, ist genau das, was sonst unsichtbar bleibt: die Aufgaben
 * behalten ihre Zuordnung (sonst wäre der Verlauf zerrissen), fallen aber aus der
 * Team-Kapazität heraus. Ohne diesen Hinweis wäre das eine stille Folge – im
 * Betrieb sind so drei Aufgaben „in Arbeit" unsichtbar liegen geblieben.
 */
function Abschaltfrage({
  person,
  abbrechen,
  bestaetigen,
}: {
  person: Benutzer
  abbrechen: () => void
  bestaetigen: () => void
}) {
  return (
    <div
      className="fixed inset-x-0 bottom-0 unter-leiste z-50 flex items-start justify-center bg-[rgb(23_22_20/0.38)] p-3 pt-[4vh] sm:p-6 sm:pt-[14vh]"
      {...hintergrundSchliesst(abbrechen)}
      role="dialog"
      aria-modal="true"
      aria-label={`${person.displayName} deaktivieren`}
    >
      <div className="w-full min-w-0 max-w-[460px] rounded-card border border-line bg-surface p-[18px] shadow-card">
        <h3 className="text-sm font-bold">{person.displayName} deaktivieren?</h3>

        <p className="mt-2 text-[12.5px] text-ink2">
          An dieser Person hängen noch{' '}
          <strong>
            {person.offeneAufgaben === 1
              ? '1 offene Aufgabe'
              : `${person.offeneAufgaben} offene Aufgaben`}
          </strong>
          .
        </p>

        <ul className="mt-2 ml-4 list-disc text-[12.5px] text-ink2">
          <li>
            Die Aufgaben <strong>bleiben zugeordnet</strong> – der Verlauf bleibt lesbar, und in der
            Auswertung steht weiter, wer was gemacht hat.
          </li>
          <li>
            Sie zählen aber <strong>nicht mehr in die Team-Kapazität</strong>. Im Leitstand
            erscheinen sie stattdessen als „Aufgaben ohne aktive Bearbeitung".
          </li>
          <li>Neu zuweisen geht ab Phase 4. Bis dahin bleibt es bei der Anzeige.</li>
        </ul>

        <div className="mt-3.5 flex gap-2">
          <Button variant="primary" size="sm" onClick={bestaetigen}>
            Deaktivieren
          </Button>
          <Button variant="ghost" size="sm" onClick={abbrechen}>
            Abbrechen
          </Button>
        </div>
      </div>
    </div>
  )
}

/**
 * **Die Arbeitswelten einer Person als Schalter** (Z-07, 26.08.2026).
 *
 * Ein Zeichen je Workspace, hell wenn Mitglied, blass wenn nicht. Klick schaltet um.
 *
 * ## Warum Chips und keine Auswahlliste
 *
 * Weil die Frage „in welchen ist er" häufiger gestellt wird als „ändere das". Eine Liste müsste
 * man erst öffnen, um sie zu lesen; drei Zeichen nebeneinander liest man im Vorbeigehen. Bei
 * mehr als etwa acht Arbeitswelten kippt das – dann gehört hier eine Auswahl hin.
 *
 * ## Persönliche stehen nicht zur Wahl
 *
 * Sie haben genau ein Mitglied, für immer ([E-232](../../../../docs/ENTSCHEIDUNGEN.md#e-232)).
 * Der Server lehnt es ohnehin ab; hier erscheint der Schalter gar nicht erst, damit niemand
 * eine Absage sucht.
 *
 * **Gäste ebenfalls nicht** – sie sehen nur, woran sie beteiligt sind, und eine Mitgliedschaft
 * erweiterte das nicht ([E-237](../../../../docs/ENTSCHEIDUNGEN.md#e-237)).
 */
function WorkspaceChips({
  person,
}: {
  person: { id: string; role: PersonRole; workspaces: string | null }
}) {
  const queryClient = useQueryClient()
  const { data: alle } = useQuery({
    queryKey: ['workspaces', 'alle'],
    queryFn: () => trpc.workspaces.alle.query(),
  })

  const setzen = useMutation({
    mutationFn: (v: { workspaceId: string; drin: boolean }) =>
      v.drin
        ? trpc.workspaces.mitgliedEntfernen.mutate({
            workspaceId: v.workspaceId,
            personId: person.id,
          })
        : trpc.workspaces.mitgliedSetzen.mutate({
            workspaceId: v.workspaceId,
            personId: person.id,
          }),
    onSettled: () => void queryClient.invalidateQueries(),
  })

  if (istGast(person.role)) return <span className="text-[11px] text-muted">–</span>

  const geteilte = (alle ?? []).filter((w) => w.art === 'geteilt' && w.isActive)
  if (geteilte.length < 2) return <span className="text-[11px] text-muted">–</span>

  const meine = (person.workspaces ?? '').split(',').filter(Boolean)

  return (
    <div className="flex flex-wrap gap-1">
      {geteilte.map((w) => {
        const drin = meine.includes(w.id)
        return (
          <button
            key={w.id}
            type="button"
            disabled={setzen.isPending}
            onClick={() => setzen.mutate({ workspaceId: w.id, drin })}
            title={`${w.label} – ${drin ? 'Mitglied, klicken zum Entfernen' : 'kein Mitglied, klicken zum Zuteilen'}`}
            className={`rounded-md px-1.5 py-0.5 text-[13px] leading-none ${
              drin ? 'bg-accent-soft' : 'opacity-30 hover:opacity-60'
            }`}
          >
            {w.icon || '📁'}
          </button>
        )
      })}
    </div>
  )
}

/**
 * **Gewählte Personen einer Arbeitswelt zuteilen – oder herausnehmen** (16.09.2026,
 * [E-355](../../../../docs/ENTSCHEIDUNGEN.md#e-355)). Nils: *„Und eine Sammelmarkierung, dass
 * ich die Leute Workspaces zuordnen kann."*
 *
 * Dieselben Häkchen wie fürs Zusammenführen – eine Auswahl, zwei Handlungen. Die Leiste steht
 * ab **einer** gewählten Person, die fürs Zusammenführen ab zwei; wer drei anhakt, sieht beide.
 *
 * **Gäste zählt der Server als übersprungen** (E-237: sie bekommen keine Mitgliedschaft, weil
 * sie ohnehin nur sehen, woran sie beteiligt sind). Die Leiste sagt es vorher, damit „12
 * gewählt, 4 zugeteilt" keine Überraschung ist. Wer einen Gast in eine Welt holen will, stuft
 * ihn erst hoch – die Rolle steht in seiner Zeile.
 */
function WeltZuordnung({
  personen,
  beiMeldung,
  fertig,
}: {
  personen: Benutzer[]
  beiMeldung: (m: { art: 'ok' | 'fehler'; text: string }) => void
  fertig: () => void
}) {
  const queryClient = useQueryClient()
  const { data: alle = [] } = useQuery({
    queryKey: ['workspaces', 'alle'],
    queryFn: () => trpc.workspaces.alle.query(),
  })
  const [weltId, setWeltId] = useState('')

  const setzen = useMutation({
    mutationFn: (drin: boolean) =>
      trpc.workspaces.mitgliedSammelSetzen.mutate({
        workspaceId: weltId,
        personIds: personen.map((p) => p.id),
        drin,
      }),
    onSuccess: (r, drin) => {
      const welt = alle.find((w) => w.id === weltId)?.label ?? 'Welt'
      beiMeldung({
        art: 'ok',
        text:
          `${r.geaendert} ${r.geaendert === 1 ? 'Person' : 'Personen'} ${
            drin ? `zu „${welt}" zugeteilt` : `aus „${welt}" herausgenommen`
          }.` +
          (r.uebersprungen > 0
            ? ` ${r.uebersprungen} übersprungen – Gäste bekommen keine Mitgliedschaft.`
            : '') +
          (r.ohneWelt > 0
            ? ` ${r.ohneWelt} nicht herausgenommen – es ist ihre letzte Arbeitswelt.`
            : ''),
      })
      fertig()
      void queryClient.invalidateQueries()
    },
    onError: (e: Error) => beiMeldung({ art: 'fehler', text: e.message }),
  })

  const geteilte = alle.filter((w) => w.art === 'geteilt' && w.isActive)
  const gaeste = personen.filter((p) => istGast(p.role)).length
  if (geteilte.length === 0) return null

  return (
    <div className="mt-3 flex flex-wrap items-center gap-2 rounded-lg bg-accent-soft px-2.5 py-2 text-[12.5px] text-accent-deep">
      <span>
        <strong>
          {personen.length} {personen.length === 1 ? 'Person' : 'Personen'} gewählt.
        </strong>{' '}
        Arbeitswelt:
      </span>
      <select
        value={weltId}
        onChange={(e) => setWeltId(e.target.value)}
        aria-label="Arbeitswelt für die Zuordnung"
        className="cursor-pointer rounded-lg border border-line2 bg-surface px-2 py-1 text-[12px] text-ink"
      >
        <option value="">Welt wählen …</option>
        {geteilte.map((w) => (
          <option key={w.id} value={w.id}>
            {w.icon ? `${w.icon} ` : ''}
            {w.label}
          </option>
        ))}
      </select>
      <Button
        variant="primary"
        size="sm"
        disabled={!weltId || setzen.isPending || gaeste === personen.length}
        onClick={() => setzen.mutate(true)}
      >
        Zuteilen
      </Button>
      <Button
        variant="ghost"
        size="sm"
        disabled={!weltId || setzen.isPending || gaeste === personen.length}
        onClick={() => setzen.mutate(false)}
      >
        Herausnehmen
      </Button>
      {gaeste > 0 && (
        <span className="text-[11px]">
          {gaeste === personen.length
            ? 'Nur Gäste gewählt – Gäste bekommen keine Mitgliedschaft, erst hochstufen.'
            : `${gaeste} ${gaeste === 1 ? 'Gast wird' : 'Gäste werden'} übersprungen.`}
        </span>
      )}
    </div>
  )
}
